import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { installOnlineSyncListener, readQueue, syncEmergencyQueue } from "../lib/sosQueue";

/**
 * Global SOS state: panel open flag + current `navigator.onLine` snapshot +
 * pending queue size. The queue sync listener is installed once at mount.
 */

const SOSContext = createContext(null);

export function SOSProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  );
  const [queueSize, setQueueSize] = useState(() => readQueue().length);
  const autoOpenedRef = useRef(false);

  const openPanel = useCallback(() => setOpen(true), []);
  const closePanel = useCallback(() => setOpen(false), []);

  // Online/offline listeners — auto-open the panel on first offline event.
  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
    };
    const onOffline = () => {
      setIsOnline(false);
      if (!autoOpenedRef.current) {
        autoOpenedRef.current = true;
        setOpen(true);
      }
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // Queue size listener — refreshed on every mutation (enqueue / clear /
  // sync). Fires via custom `sos.queue.change` event from `lib/sosQueue`.
  useEffect(() => {
    const onChange = (e) => {
      const size = e?.detail?.size;
      if (typeof size === "number") setQueueSize(size);
      else setQueueSize(readQueue().length);
    };
    window.addEventListener("sos.queue.change", onChange);
    return () => window.removeEventListener("sos.queue.change", onChange);
  }, []);

  // Install the online sync listener globally so queued messages flush
  // even when the SOS panel is not mounted.
  useEffect(() => {
    const cleanup = installOnlineSyncListener((result) => {
      if (result?.confirmed > 0) {
        try {
          window.dispatchEvent(
            new CustomEvent("sos.synced", {
              detail: { count: result.confirmed },
            })
          );
        } catch (_e) {
          /* ignore */
        }
      }
    });
    return cleanup;
  }, []);

  const syncNow = useCallback(async () => {
    return syncEmergencyQueue();
  }, []);

  const value = useMemo(
    () => ({
      open,
      openPanel,
      closePanel,
      isOnline,
      queueSize,
      syncNow,
    }),
    [open, openPanel, closePanel, isOnline, queueSize, syncNow]
  );

  return <SOSContext.Provider value={value}>{children}</SOSContext.Provider>;
}

export function useSOS() {
  const ctx = useContext(SOSContext);
  if (!ctx) throw new Error("useSOS debe usarse dentro de SOSProvider");
  return ctx;
}
